// The Sun's real orientation, for placing NOAA's active regions on the
// viewer's Sun and on the /space-weather/ page's disc.
//
// The rotation: the IAU Working Group on Cartographic Coordinates and
// Rotational Elements (Archinal et al. 2018, Celest. Mech. Dyn. Astr. 130:22):
// the Sun's north pole at right ascension 286.13 and declination 63.87
// (ICRF), and its prime meridian at W = 84.176 + 14.1844000 d degrees, d days
// from J2000. That prime meridian is Carrington's, so a heliographic
// latitude B and Carrington longitude L place a point on the Sun at any
// moment. Carrington longitude grows in the direction of rotation, so the
// longitude facing the Earth (L0) falls by about 13.2 degrees a day, and a
// region west of the central meridian has a larger L: L = L0 + its west
// longitude (NOAA's regions check this, e.g. S15E34 at L 102 and N09W81 at
// 217 on 2026-10-12, L0 136).
//
// Regions are carried from the moment NOAA measured them with differential
// rotation (Snodgrass and Ulrich 1990, ApJ 351, 309: 14.713 - 2.396 sin^2 B
// - 1.787 sin^4 B degrees a day, sidereal; the rate the modelled spots use
// in shaders/sun.js), against the Carrington rate of 14.1844.
//
// The scene is J2000 ecliptic: +X the equinox, +Y ecliptic north, -Z
// ecliptic longitude 90 (CLAUDE.md, "Earth orientation").

const DEG = Math.PI / 180;
const OBLIQUITY_J2000 = 23.4392911 * DEG;
const POLE_RA = 286.13 * DEG;
const POLE_DEC = 63.87 * DEG;
const W0 = 84.176;
export const CARRINGTON_RATE = 14.1844;
const J2000_MS = Date.UTC(2000, 0, 1, 12, 0, 0);

const dot = (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const cross = (a, b) => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
const norm = (a) => { const l = Math.hypot(a[0], a[1], a[2]) || 1; return [a[0] / l, a[1] / l, a[2] / l]; };
const wrap360 = (x) => ((x % 360) + 360) % 360;

// ICRF equatorial -> scene (J2000 ecliptic, y up).
export function equatorialToScene([x, y, z]) {
  const c = Math.cos(OBLIQUITY_J2000);
  const s = Math.sin(OBLIQUITY_J2000);
  const ye = y * c + z * s;
  const ze = -y * s + z * c;
  return [x, ze, -ye];
}

// The Sun's pole and equatorial frame (ICRF) and its prime meridian angle.
function frame(ms) {
  const d = (ms - J2000_MS) / 86400000;
  const pole = [Math.cos(POLE_DEC) * Math.cos(POLE_RA), Math.cos(POLE_DEC) * Math.sin(POLE_RA), Math.sin(POLE_DEC)];
  const node = [-Math.sin(POLE_RA), Math.cos(POLE_RA), 0];
  return { pole, node, east: cross(pole, node), W: wrap360(W0 + CARRINGTON_RATE * d) };
}

// A heliographic point (B, Carrington L, degrees) as a unit vector, ICRF.
export function heliographicEquatorial(B, L, ms) {
  const f = frame(ms);
  const th = (f.W + L) * DEG;
  const cb = Math.cos(B * DEG);
  const sb = Math.sin(B * DEG);
  return [0, 1, 2].map((i) => cb * (Math.cos(th) * f.node[i] + Math.sin(th) * f.east[i]) + sb * f.pole[i]);
}

export const heliographicScene = (B, L, ms) => equatorialToScene(heliographicEquatorial(B, L, ms));
export const sunPoleScene = (ms) => equatorialToScene(frame(ms).pole);

// The Earth seen from the Sun (ICRF unit vector): the Sun's apparent
// ecliptic longitude (Astronomical Almanac, low precision) taken back to
// J2000 by the general precession.
export function earthFromSun(ms) {
  const n = (ms - J2000_MS) / 86400000;
  const g = (357.528 + 0.9856003 * n) * DEG;
  const lambdaDate = 280.46 + 0.9856474 * n + 1.915 * Math.sin(g) + 0.02 * Math.sin(2 * g);
  const lambda = (lambdaDate - 1.3969713 * (n / 36525)) * DEG;
  const sun = [Math.cos(lambda), Math.sin(lambda) * Math.cos(OBLIQUITY_J2000), Math.sin(lambda) * Math.sin(OBLIQUITY_J2000)];
  return [-sun[0], -sun[1], -sun[2]];
}

// The disc as seen from the Earth: B0 (heliographic latitude of its
// centre), L0 (Carrington longitude of its central meridian) and P (the
// pole's position angle, degrees east of celestial north).
export function discCentre(ms) {
  const f = frame(ms);
  const e = earthFromSun(ms);
  const B0 = Math.asin(dot(e, f.pole)) / DEG;
  const L0 = wrap360(Math.atan2(dot(e, f.east), dot(e, f.node)) / DEG - f.W);
  // On the sky, looking from the Earth toward the Sun (-e): celestial north
  // and east, and the pole projected among them.
  const toSun = e.map((v) => -v);
  const northSky = norm([0, 0, 1].map((v, i) => v - toSun[i] * toSun[2]));
  const eastSky = cross(northSky, toSun);
  const P = Math.atan2(dot(f.pole, eastSky), dot(f.pole, northSky)) / DEG;
  return { B0, L0, P };
}

// Differential rotation against the Carrington frame, degrees a day.
export function driftRate(B) {
  const s2 = Math.sin(B * DEG) ** 2;
  return 14.713 - 2.396 * s2 - 1.787 * s2 * s2 - CARRINGTON_RATE;
}

// A region's Carrington longitude at ms, from NOAA's at the time it was
// measured (or from its distance from the central meridian then).
export function regionLongitude(region, observedMs, ms) {
  const L = region.carr !== null && region.carr !== undefined ? region.carr : wrap360(discCentre(observedMs).L0 + region.lonCM);
  return wrap360(L + driftRate(region.lat) * ((ms - observedMs) / 86400000));
}

// Where a point lies on the disc seen from the Earth, in solar radii, solar
// north up and west to the right (as SDO's images are shown), and whether
// it faces us.
export function discPosition(B, L, ms) {
  const { B0, L0 } = discCentre(ms);
  const b = B * DEG;
  const b0 = B0 * DEG;
  const dl = (L - L0) * DEG;
  return {
    x: Math.cos(b) * Math.sin(dl),
    y: Math.sin(b) * Math.cos(b0) - Math.cos(b) * Math.sin(b0) * Math.cos(dl),
    front: Math.cos(b) * Math.cos(b0) * Math.cos(dl) + Math.sin(b) * Math.sin(b0) > 0,
  };
}

// How a NOAA region is drawn, from its McIntosh class, area and extent
// (McIntosh 1990, Sol. Phys. 125, 251): the Zurich letter says whether it
// is one spot (A, H) or a bipolar group with penumbra on neither end (B),
// one end (C) or both (D, E, F: under 10, 10 to 15 and over 15 degrees
// long); the second letter the leader's penumbra (x none, r rudimentary,
// s and a small, h and k large). Area is in millionths of the solar
// hemisphere, corrected for foreshortening, so a unit sphere's spots cover
// area * 2 pi * 1e-6 steradians: the leader takes about half of it, the
// follower a third or so (nearly as much in a big h or k group), a few
// small spots the rest. A spot's umbra is 1/2.45
// of its penumbra's radius (about a sixth of its area), as shaders/sun.js
// draws them. The group is tilted by Joy's law, about half its latitude,
// the leader nearer the equator.
export function spotModel(region) {
  const cls = String(region.cls || '');
  const z = cls[0] || '';
  const p = cls[1] || 'x';
  const area = Math.max(0, region.area || 0);
  if (!area || !z) return { plageOnly: true, rLead: 0, sep: 0.06, tilt: 0, penLead: 0, penFoll: -1, rFoll: 0, small: 0 };
  const total = area * 2 * Math.PI * 1e-6;
  const unipolar = z === 'A' || z === 'H';
  // A big bipolar group with a large penumbra (h, k) often has two big
  // umbrae, leader and follower nearly matched.
  const matched = !unipolar && 'DEF'.includes(z) && (p === 'h' || p === 'k');
  const leadShare = unipolar ? 0.85 : matched ? 0.42 : 0.5;
  const follShare = unipolar ? 0 : matched ? 0.38 : 0.3;
  const penLead = { x: 0, r: 0.35, s: 1, a: 0.85, h: 1, k: 0.9 }[p] ?? 0;
  const penFoll = unipolar ? -1 : (z === 'D' || z === 'E' || z === 'F') ? 0.8 : 0;
  const radiusFor = (a, pen) => Math.sqrt(a / Math.PI) / (pen > 0.01 ? 2.45 : 1);
  const extent = region.extent || { A: 2, H: 3, B: 4, C: 6, D: 8, E: 12, F: 17 }[z] || 4;
  return {
    plageOnly: false,
    rLead: radiusFor(total * leadShare, penLead),
    // Leader and follower centres sit a little inside the group's ends.
    sep: unipolar ? 0 : Math.max(2, extent * 0.8) * DEG,
    tilt: 0.5 * Math.abs(region.lat) * DEG,
    penLead,
    penFoll,
    rFoll: unipolar ? 0 : radiusFor(total * follShare, penFoll),
    small: Math.max(0, Math.min(3, (region.spots || 1) - (unipolar ? 1 : 2))),
  };
}
